import React, { useState } from 'react';

import { notify } from '@NOTIFICATION/Notificator';

import { EntityTable, EntityFormModal } from '@COMPONENTS/SHARED/EntityManagement';

import {
    useGetAllAreasAdmin,
    useCreateArea,
    useUpdateArea,
    useDeleteArea,
} from '@SERVICES';
import { Area } from '@INTERFACES/api/areas';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

function AreasTab() {
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [editingArea, setEditingArea] = useState<Area | null>(null);

    const { data: areas = [], isLoading, refetch } = useGetAllAreasAdmin();
    const [createArea, { isLoading: isCreating }] = useCreateArea();
    const [updateArea, { isLoading: isUpdating }] = useUpdateArea();
    const [deleteArea] = useDeleteArea();

    const columns = [
        {
            key: 'id' as keyof Area,
            header: 'ID',
        },
        {
            key: 'name' as keyof Area,
            header: 'Name',
        },
    ];

    const formFields = [
        {
            key: 'name',
            label: 'Name',
            type: 'text' as const,
            required: true,
            placeholder: 'Enter area name',
        },
    ];

    const handleCreate = () => {
        setEditingArea(null);
        setIsFormModalOpen(true);
    };

    const handleEdit = (area: Area) => {
        setEditingArea(area);
        setIsFormModalOpen(true);
    };

    const handleDelete = async (area: Area) => {
        if (window.confirm(`Are you sure you want to delete "${area.name}"?`)) {
            try {
                await deleteArea(area.id).unwrap();
                notify.success('Area deleted successfully!');
                refetch();
            } catch (error) {
                // Extract specific error message from API response
                let errorMessage = 'Failed to delete area!';
                
                if (error && typeof error === 'object' && 'status' in error) {
                    const fetchError = error as FetchBaseQueryError;
                    if (typeof fetchError.status === 'number' && fetchError.data) {
                        // Try to extract the message from the API response
                        const errorData = fetchError.data as any;
                        if (errorData && typeof errorData === 'object' && errorData.message) {
                            errorMessage = errorData.message;
                        }
                    }
                }
                
                notify.error(errorMessage);
            }
        }
    };

    const handleFormSubmit = async (formData: any) => {
        try {
            if (editingArea) {
                await updateArea({ id: editingArea.id, ...formData }).unwrap();
                notify.success('Area updated successfully!');
            } else {
                await createArea(formData).unwrap();
                notify.success('Area created successfully!');
            }
            setIsFormModalOpen(false);
            setEditingArea(null);
            refetch();
        } catch (error) {
            notify.error(`Failed to ${editingArea ? 'update' : 'create'} area!`);
        }
    };

    const handleFormClose = () => {
        setIsFormModalOpen(false);
        setEditingArea(null);
    };

    return (
        <div style={{ padding: '24px' }}>
            <EntityTable
                data={areas}
                columns={columns}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onCreate={handleCreate}
                isLoading={isLoading}
                entityName="Area"
            />

            <EntityFormModal
                isOpen={isFormModalOpen}
                onClose={handleFormClose}
                onSubmit={handleFormSubmit}
                title={editingArea ? 'Edit Area' : 'Create Area'}
                fields={formFields}
                initialData={editingArea}
                isLoading={isCreating || isUpdating}
            />
        </div>
    );
}

export default AreasTab;
